import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Mail, MapPin, Phone, Send, Clock3 } from 'lucide-react';

const interests = ['Private Residences', 'Commercial', 'Land Consultation', 'Investment'];

const contactDetails = [
  {
    title: 'Studio line',
    value: '+91 95352 74780',
    icon: Phone,
  },
  {
    title: 'Private mail',
    value: 'surakshabd2023@gmail.com',
    icon: Mail,
  },
  {
    title: 'Studio address',
    value: 'Site No. 33, Coconut Garden, Kodigehalli Main Road, Ayyapanagara, K. R. Puram, Bangalore - 560 036',
    icon: MapPin,
  },
];

export default function Contact() {
  const [selectedInterest, setSelectedInterest] = useState(interests[0]);
  const logoSrc = `${process.env.PUBLIC_URL}/asserts/logo-transparent.png`;

  return (
    <section className="relative overflow-hidden pb-28 pt-36 md:pb-32 md:pt-44">
      <div className="floating-orb absolute left-[-8rem] top-24 h-80 w-80 rounded-full bg-accent/14" />
      <div className="floating-orb absolute right-[-8rem] bottom-10 h-72 w-72 rounded-full bg-primary/[0.06]" />

      <div className="section-shell relative">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] xl:items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
            className="dark-panel overflow-hidden p-8 md:p-10"
          >
            <div className="mb-10 inline-flex items-center gap-4 rounded-full border border-white/10 bg-white/5 px-4 py-4">
              <img
                src={logoSrc}
                alt="Suraksha Builders logo"
                className="h-14 w-14 rounded-full border border-white/10 bg-white/8 p-2 object-contain"
              />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">
                  Suraksha Builders &amp; Developers
                </p>
                <p className="mt-1 font-serif text-lg text-secondary">Bangalore consultations by appointment.</p>
              </div>
            </div>

            <span className="editorial-kicker text-gold-light before:bg-gold-light">Contact</span>
            <h1 className="mt-7 text-balance text-4xl leading-tight text-secondary sm:text-5xl md:text-6xl">
              Tell us what you are building, acquiring, or evaluating.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-secondary/70 md:text-lg">
              The first conversation is structured around budget, timeline, site context, and how much guidance you need from the studio. That keeps the next step concrete.
            </p>

            <div className="mt-10 space-y-4">
              {contactDetails.map((item) => (
                <div key={item.title} className="rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-gold-light">
                      <item.icon size={18} />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-light/80">
                        {item.title}
                      </p>
                      <p className="mt-2 text-base leading-7 text-secondary/80">{item.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
              <div className="flex items-center gap-3 text-gold-light">
                <Clock3 size={18} />
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em]">Response window</p>
              </div>
              <p className="mt-3 text-base leading-7 text-secondary/70">
                We typically reply within one business day and can schedule in-person visits for serious project discussions.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, delay: 0.08 }}
            className="soft-panel p-8 md:p-10 lg:p-12"
          >
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                  Your name
                </label>
                <input
                  type="text"
                  placeholder="Ex. Ananya Rao"
                  className="mt-3 w-full rounded-[1.25rem] border border-white/10 bg-white/12 px-5 py-3 text-base text-primary outline-none transition-colors placeholder:text-secondary/35 focus:border-accent/35"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="mt-3 w-full rounded-[1.25rem] border border-white/10 bg-white/12 px-5 py-3 text-base text-primary outline-none transition-colors placeholder:text-secondary/35 focus:border-accent/35"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                  Phone number
                </label>
                <input
                  type="tel"
                  placeholder="+91 95352 74780"
                  className="mt-3 w-full rounded-[1.25rem] border border-white/10 bg-white/12 px-5 py-3 text-base text-primary outline-none transition-colors placeholder:text-secondary/35 focus:border-accent/35"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                  Preferred city
                </label>
                <input
                  type="text"
                  placeholder="Bangalore"
                  className="mt-3 w-full rounded-[1.25rem] border border-white/10 bg-white/12 px-5 py-3 text-base text-primary outline-none transition-colors placeholder:text-secondary/35 focus:border-accent/35"
                />
              </div>
            </div>

            <div className="mt-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                What do you need?
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {interests.map((item) => {
                  const isSelected = item === selectedInterest;

                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setSelectedInterest(item)}
                      className={isSelected ? 'rounded-full bg-accent px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-secondary' : 'rounded-full border border-white/10 bg-white/6 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-secondary/70'}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8">
              <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-secondary/40">
                Project details
              </label>
              <textarea
                rows={6}
                defaultValue={`I would like to discuss ${selectedInterest.toLowerCase()} for...`}
                className="mt-3 w-full rounded-[1.5rem] border border-white/10 bg-white/12 px-5 py-4 text-base leading-7 text-primary outline-none transition-colors placeholder:text-secondary/35 focus:border-accent/35"
              />
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-white/10 bg-white/6 p-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">Selected track</p>
              <p className="mt-2 font-serif text-2xl text-secondary">{selectedInterest}</p>
              <p className="mt-3 text-sm leading-7 text-secondary/60">
                Include site size, preferred localities, timeline, and whether you need design support, development partnership, or project updates.
              </p>
            </div>

            <button className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full copper-gradient px-7 py-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-primary shadow-[0_20px_50px_rgba(170,109,39,0.28)] transition-transform duration-300 hover:-translate-y-0.5">
              Send Private Request
              <Send size={16} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
